import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useDocument,
  useUpdateDocument,
  useArchiveDocument,
  useDownloadDocument,
} from '../../../platform/api/hooks';
import { actorLabel, asRecord, str } from '../../../platform/api/utils';
import { downloadBlob } from '../../../shared/download';
import {
  DOCUMENT_STATUS_LABELS,
  DOCUMENT_VISIBILITY_LABELS,
  labelOf,
} from '../../../shared/labels';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  Dd,
  Dt,
  SkeletonDetail,
  dangerBtn,
  dlGrid,
  fieldLabel,
  fmtDate,
  formatBytes,
  friendlyMime,
  inputCss,
  linkBtn,
  modalOverlay,
  modalStyle,
  primaryBtn,
  secondaryBtn,
} from './document-detail.support';

export function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useDocument(id ?? '');
  const updateDocument = useUpdateDocument();
  const archiveDocument = useArchiveDocument();
  const downloadDocument = useDownloadDocument();

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editVisibility, setEditVisibility] = useState('');
  const [downloadError, setDownloadError] = useState('');

  if (isLoading) return <SkeletonDetail />;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 800 }}>
        <button
          type="button"
          onClick={() => {
            void navigate('/documentos');
          }}
          style={linkBtn}
        >
          ← Voltar para documentos
        </button>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)' }}>Não foi possível carregar o documento.</p>
        </Section>
      </div>
    );
  }

  const doc = asRecord(data);
  const title = str(doc.title);
  const fileName = str(doc.originalFileName);
  const isActive = str(doc.status) === 'ACTIVE';

  function startEdit() {
    setEditTitle(title);
    setEditDesc(str(doc.description));
    setEditVisibility(str(doc.visibility, 'INTERNAL'));
    setEditing(true);
  }

  function handleSave() {
    if (!id) return;
    const payload: Record<string, string> = {};
    if (editTitle !== title) payload.title = editTitle;
    if (editDesc !== str(doc.description)) payload.description = editDesc;
    if (editVisibility !== str(doc.visibility)) payload.visibility = editVisibility;
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    updateDocument.mutate({ id, data: payload });
    setEditing(false);
  }

  async function handleDownload() {
    if (!id) return;
    setDownloadError('');
    try {
      const blob = await downloadDocument.mutateAsync(id);
      downloadBlob(blob, fileName);
    } catch {
      setDownloadError('Erro ao baixar o documento.');
    }
  }

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 800 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/documentos');
        }}
        style={linkBtn}
      >
        ← Voltar para documentos
      </button>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{title}</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--faint)', margin: '0.25rem 0 0' }}>
            {fileName} &middot; {formatBytes(doc.sizeBytes)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isActive && (
            <button type="button" onClick={startEdit} style={secondaryBtn}>
              Editar
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              void handleDownload();
            }}
            style={primaryBtn}
            disabled={downloadDocument.isPending}
          >
            {downloadDocument.isPending ? 'Baixando...' : 'Download'}
          </button>
        </div>
      </div>

      {downloadError && (
        <p style={{ color: 'var(--danger)', fontSize: '0.8rem', margin: '0 0 1rem' }}>
          {downloadError}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Edit modal */}
        {editing && (
          <div style={modalOverlay}>
            <div style={modalStyle}>
              <h3 style={{ margin: '0 0 1rem' }}>Editar documento</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={fieldLabel}>
                  Título
                  <input
                    value={editTitle}
                    onChange={(e) => {
                      setEditTitle(e.target.value);
                    }}
                    style={inputCss}
                  />
                </label>
                <label style={fieldLabel}>
                  Descrição
                  <textarea
                    value={editDesc}
                    onChange={(e) => {
                      setEditDesc(e.target.value);
                    }}
                    style={{ ...inputCss, minHeight: 64, resize: 'vertical' }}
                  />
                </label>
                <label style={fieldLabel}>
                  Visibilidade
                  <select
                    value={editVisibility}
                    onChange={(e) => {
                      setEditVisibility(e.target.value);
                    }}
                    style={inputCss}
                  >
                    {Object.entries(DOCUMENT_VISIBILITY_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setEditing(false);
                    }}
                    style={secondaryBtn}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    style={primaryBtn}
                    disabled={updateDocument.isPending}
                  >
                    {updateDocument.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Section title="Informações">
          <dl style={dlGrid}>
            <Dt>Título</Dt>
            <Dd>{title}</Dd>
            <Dt>Arquivo</Dt>
            <Dd style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{fileName || '—'}</Dd>
            <Dt>Tipo</Dt>
            <Dd>{friendlyMime(str(doc.mimeType))}</Dd>
            <Dt>Tamanho</Dt>
            <Dd>{formatBytes(doc.sizeBytes)}</Dd>
            {str(doc.description) && (
              <>
                <Dt>Descrição</Dt>
                <Dd>{str(doc.description)}</Dd>
              </>
            )}
          </dl>
        </Section>

        <Section title="Status e controle">
          <dl style={dlGrid}>
            <Dt>Status</Dt>
            <Dd>
              <Badge
                label={labelOf(DOCUMENT_STATUS_LABELS, doc.status)}
                variant={isActive ? 'success' : 'default'}
              />
            </Dd>
            <Dt>Visibilidade</Dt>
            <Dd>
              <Badge label={labelOf(DOCUMENT_VISIBILITY_LABELS, doc.visibility)} variant="info" />
            </Dd>
            <Dt>Enviado por</Dt>
            <Dd>{actorLabel(doc.uploadedByDisplay, doc.uploadedBy)}</Dd>
            <Dt>Criado em</Dt>
            <Dd>{fmtDate(doc.createdAt)}</Dd>
            <Dt>Atualizado em</Dt>
            <Dd>{fmtDate(doc.updatedAt)}</Dd>
            {!!doc.expiresAt && (
              <>
                <Dt>Expira em</Dt>
                <Dd style={{ color: 'var(--warning)' }}>{fmtDate(doc.expiresAt)}</Dd>
              </>
            )}
          </dl>

          {isActive && (
            <div style={{ marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      'Arquivar este documento? Ele não aparecerá mais nas listagens principais.',
                    )
                  ) {
                    archiveDocument.mutate(id);
                  }
                }}
                disabled={archiveDocument.isPending}
                style={dangerBtn}
              >
                {archiveDocument.isPending ? 'Arquivando...' : 'Arquivar documento'}
              </button>
            </div>
          )}
          {!isActive && (
            <p style={{ fontSize: '0.8rem', color: 'var(--faint)', marginTop: '0.75rem' }}>
              Este documento está arquivado e não aparece nas listagens principais.
            </p>
          )}
        </Section>
      </div>
    </div>
  );
}
